import { useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { FileCode2, Upload, CheckCircle2, Eye, Trash2, Download, Zap } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Spinner } from '@/shared/components/ui/Spinner';
import {
  usePayslipTemplates, useUploadPayslipTemplate,
  useActivatePayslipTemplate, useDeletePayslipTemplate, useTemplatePreview
} from './hooks/usePayroll';
import type { PayslipTemplate } from './payrollTypes';

const STARTER_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 13px; color: #111; background: #fff; padding: 40px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid {{accent_color}}; padding-bottom: 16px; margin-bottom: 20px; }
  .company-name { font-size: 20px; font-weight: bold; color: {{accent_color}}; }
  .company-sub { font-size: 11px; color: #666; margin-top: 4px; }
  .payslip-title { text-align: right; }
  .payslip-title h2 { font-size: 16px; color: {{accent_color}}; }
  .payslip-title p { font-size: 11px; color: #666; margin-top: 2px; }
  .employee-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; margin-bottom: 20px; padding: 12px; background: #f8f9fa; border-radius: 6px; }
  .info-row { font-size: 12px; }
  .info-label { color: #666; }
  .info-value { font-weight: 600; color: #111; }
  .salary-section { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 20px; }
  .section-title { font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid; padding-bottom: 6px; margin-bottom: 10px; }
  .earnings-title { color: #16a34a; border-color: #16a34a; }
  .deductions-title { color: #dc2626; border-color: #dc2626; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  td { padding: 4px 0; }
  td:last-child { text-align: right; font-family: monospace; }
  .total-row td { border-top: 1px solid #ccc; padding-top: 6px; margin-top: 4px; font-weight: bold; }
  .net-salary { display: flex; justify-content: space-between; align-items: center; background: {{accent_color}}15; border: 1px solid {{accent_color}}40; border-radius: 6px; padding: 12px 16px; margin-top: 8px; }
  .net-label { font-size: 14px; font-weight: bold; }
  .net-value { font-size: 20px; font-weight: bold; color: {{accent_color}}; font-family: monospace; }
  .signatures { display: flex; justify-content: space-between; margin-top: 48px; padding-top: 16px; }
  .signature-line { text-align: center; width: 180px; }
  .signature-line div { border-top: 1px solid #999; padding-top: 6px; font-size: 11px; color: #666; }
</style>
</head>
<body>
<div class="header">
  <div>
    {{#if company_logo_url}}<img src="{{company_logo_url}}" style="height:48px; margin-bottom:8px;" />{{/if}}
    <div class="company-name">{{company_name}}</div>
    <div class="company-sub">{{company_address}}</div>
    {{#if company_gst}}<div class="company-sub">GST: {{company_gst}}</div>{{/if}}
  </div>
  <div class="payslip-title">
    <h2>PAYSLIP</h2>
    <p>Month: {{month_year}}</p>
    <p>Date: {{payslip_date}}</p>
    <p>Status: {{status}}</p>
  </div>
</div>

<div class="employee-info">
  <div class="info-row"><span class="info-label">Employee Name: </span><span class="info-value">{{employee_name}}</span></div>
  <div class="info-row"><span class="info-label">Employee ID: </span><span class="info-value">{{employee_id}}</span></div>
  <div class="info-row"><span class="info-label">Designation: </span><span class="info-value">{{designation}}</span></div>
  <div class="info-row"><span class="info-label">Department: </span><span class="info-value">{{department}}</span></div>
  <div class="info-row"><span class="info-label">Working Days: </span><span class="info-value">{{working_days}}</span></div>
  <div class="info-row"><span class="info-label">Leave Days: </span><span class="info-value">{{leave_days}}</span></div>
  <div class="info-row"><span class="info-label">Bank: </span><span class="info-value">{{bank_name}}</span></div>
  <div class="info-row"><span class="info-label">IFSC: </span><span class="info-value">{{ifsc_code}}</span></div>
</div>

<div class="salary-section">
  <div>
    <div class="section-title earnings-title">Earnings</div>
    <table>
      <tbody>{{earnings_table}}</tbody>
      <tfoot><tr class="total-row"><td>Total Earnings</td><td>{{total_earnings}}</td></tr></tfoot>
    </table>
  </div>
  <div>
    <div class="section-title deductions-title">Deductions</div>
    <table>
      <tbody>{{deductions_table}}</tbody>
      <tfoot><tr class="total-row"><td>Total Deductions</td><td>{{total_deductions}}</td></tr></tfoot>
    </table>
  </div>
</div>

<div class="net-salary">
  <div class="net-label">Net Salary Payable</div>
  <div class="net-value">{{net_salary}}</div>
</div>

<div class="signatures">
  <div class="signature-line"><div>Authorised Signatory</div></div>
  <div class="signature-line"><div>Employee Signature</div></div>
</div>
</body>
</html>`;

export function PayslipTemplateTab() {
  const { data: templates = [], isLoading } = usePayslipTemplates();
  const upload = useUploadPayslipTemplate();
  const activate = useActivatePayslipTemplate();
  const deleteT = useDeletePayslipTemplate();
  const preview = useTemplatePreview();

  const fileRef = useRef<HTMLInputElement>(null);
  const [newName, setNewName] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const handleUpload = async (file: File) => {
    if (!file.name.match(/\.html?$/i)) { toast.error('Only .html files are allowed'); return; }
    try {
      await upload.mutateAsync({ file, name: newName || undefined });
      toast.success('Template uploaded!');
      setNewName('');
    } catch {
      toast.error('Upload failed');
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await activate.mutateAsync(id);
      toast.success('Template set as active!');
    } catch {
      toast.error('Failed to activate');
    }
  };

  const handleDelete = async (t: PayslipTemplate) => {
    if (!confirm(`Delete template "${t.name}"?`)) return;
    try {
      await deleteT.mutateAsync(t.id);
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handlePreview = async (id: string) => {
    try {
      const result = await preview.mutateAsync(id);
      setPreviewHtml(result.html);
    } catch {
      toast.error('Failed to load preview');
    }
  };

  const downloadStarter = () => {
    const blob = new Blob([STARTER_TEMPLATE], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'payslip-starter-template.html';
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-on-surface flex items-center gap-2">
            <FileCode2 className="w-4 h-4 text-primary" /> Custom Payslip Templates
          </h3>
          <p className="text-body-md text-on-surface-variant mt-1 max-w-lg">
            Upload your own branded HTML template. The system will inject real payslip data into <code className="text-body-sm bg-surface-variant px-1 py-0.5 rounded">{'{{placeholders}}'}</code>.
            Only one template can be active at a time.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadStarter}>
          <Download className="w-3.5 h-3.5" /> Starter Template
        </Button>
      </div>

      {/* Upload area */}
      <div className="rounded-lg border border-border p-4 space-y-3">
        <h4 className="text-body-md font-medium text-on-surface">Upload New Template</h4>
        <div className="flex items-center gap-3">
          <Input
            name="template-name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Template name (optional)"
            className="flex-1"
          />
          <Button variant="outline" onClick={() => fileRef.current?.click()} loading={upload.isPending}>
            <Upload className="w-4 h-4" /> Choose .html File
          </Button>
          <input ref={fileRef} type="file" accept=".html,.htm" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
        </div>
        <p className="text-body-sm text-on-surface-variant">
          Use <code className="bg-surface-variant px-1 rounded">{'{{employee_name}}'}</code>,{' '}
          <code className="bg-surface-variant px-1 rounded">{'{{earnings_table}}'}</code>,{' '}
          <code className="bg-surface-variant px-1 rounded">{'{{net_salary}}'}</code>, etc.
          Download the starter template to see all available placeholders.
        </p>
      </div>

      {/* Template list */}
      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : templates.length === 0 ? (
        <div className="text-center py-10 text-on-surface-variant">
          <FileCode2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-body-md">No custom templates yet.</p>
          <p className="text-body-sm mt-1">Download the starter template, customise it, then upload it here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((t: PayslipTemplate) => (
            <div key={t.id} className={`flex items-center gap-4 rounded-lg border p-4 transition-colors ${t.is_active ? 'border-primary/40 bg-primary/5' : 'border-outline'}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-on-surface truncate">{t.name}</span>
                  {t.is_active && (
                    <span className="flex items-center gap-1 text-body-sm font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                      <Zap className="w-3 h-3" /> Active
                    </span>
                  )}
                </div>
                <p className="text-body-sm text-on-surface-variant mt-0.5">
                  Uploaded {t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN') : '—'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handlePreview(t.id)} loading={preview.isPending}>
                  <Eye className="w-3.5 h-3.5" /> Preview
                </Button>
                {!t.is_active && (
                  <Button size="sm" onClick={() => handleActivate(t.id)} loading={activate.isPending}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Activate
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => handleDelete(t)}>
                  <Trash2 className="w-3.5 h-3.5 text-danger-text" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Placeholder reference */}
      <details className="rounded-lg border border-border p-4">
        <summary className="text-body-md font-medium text-on-surface cursor-pointer select-none">
          Available Template Placeholders
        </summary>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-body-sm">
          {[
            ['{{company_name}}', 'Company name (from Settings)'],
            ['{{company_address}}', 'Company address'],
            ['{{company_gst}}', 'GST number'],
            ['{{company_logo_url}}', 'Logo image URL'],
            ['{{accent_color}}', 'Brand accent color (hex)'],
            ['{{employee_name}}', 'Employee full name'],
            ['{{employee_id}}', 'Employee ID code'],
            ['{{designation}}', 'Job title / designation'],
            ['{{department}}', 'Department'],
            ['{{email}}', 'Employee email'],
            ['{{month_year}}', 'Pay period (e.g. 2026-07)'],
            ['{{payslip_date}}', 'Payslip generation date'],
            ['{{bank_name}}', 'Bank name'],
            ['{{bank_account}}', 'Account number'],
            ['{{ifsc_code}}', 'IFSC code'],
            ['{{working_days}}', 'Working days this month'],
            ['{{leave_days}}', 'Leave days taken'],
            ['{{earnings.Basic}}', 'Individual earning amount'],
            ['{{deductions.Income Tax}}', 'Individual deduction amount'],
            ['{{earnings_table}}', '<tr> rows for all earnings'],
            ['{{deductions_table}}', '<tr> rows for all deductions'],
            ['{{total_earnings}}', 'Sum of earnings'],
            ['{{total_deductions}}', 'Sum of deductions'],
            ['{{net_salary}}', 'Net pay (formatted)'],
            ['{{currency}}', 'Currency symbol (₹)'],
            ['{{status}}', 'draft / finalized'],
          ].map(([k, v]) => (
            <div key={k} className="flex gap-2 py-0.5">
              <code className="text-primary bg-primary/5 px-1 rounded shrink-0">{k}</code>
              <span className="text-on-surface-variant">{v}</span>
            </div>
          ))}
        </div>
      </details>

      {/* Preview Modal */}
      <Modal open={!!previewHtml} onClose={() => setPreviewHtml(null)} title="Template Preview" size="lg">
        <div className="overflow-auto max-h-[75vh] rounded-lg border border-border bg-card">
          {previewHtml && (
            <iframe
              srcDoc={previewHtml}
              title="Payslip Template Preview"
              className="w-full min-h-[600px] border-0"
              sandbox="allow-same-origin"
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
